import * as React from 'react';
import { useEffect, useMemo } from 'react';
import { Helmet } from 'react-helmet-async';
import { Flex, Spin } from 'antd';
import styled from 'styled-components';
import { useSelector } from 'react-redux';
import { LoadingOutlined } from '@ant-design/icons';
import { NavLink, NavLinkProps, useNavigate } from 'react-router-dom';
import { useParams } from 'react-router';
import { DivMinWidth, FlexPadStyled } from '../App';
import { Breadcrumbs } from '../components/Breadcrumbs';
import { LogoBC } from '../components/LogoBC';
import { colors, fonts } from '../assets/constStyles';
import { TabProjectAwards } from '../components/tab/TabProjectAwards';
import { TabOtherAwards } from '../components/tab/TabOtherAwards';
import { TabEventPhotosAwards } from '../components/tab/TabEventPhotosAwards';
import { RootState } from '../store';
import { TabMagazine } from '../components/tab/TabMagazine';

export const TabPanelsWrapper = styled.div`
  position: relative;
  width: 100%;
  max-width: 1200px;
  min-height: 500px;
  box-sizing: border-box;
`;

const Nav = styled.nav`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin: 16px 0 24px;
`;

const NavItem = styled(NavLink)`
  padding: 8px 14px;
  //border-radius: 9999px;
  border-bottom: 1px solid rgba(0,0,0,0.1);
  text-decoration: none;
  text-transform: uppercase;
  font-weight: 600;
  line-height: 1;
  transition: transform .05s ease;
  &:hover { 
    transform: translateY(-1px);
    border-color: ${colors.colorRed};
  }
  &.active {
    color: ${colors.colorRed};
    border-color: ${colors.colorRed};
    box-shadow: 0 1px 0 rgba(0,0,0,0.08);
  }
`;

const getNavClass: NavLinkProps['className'] = ({ isActive }) => (isActive ? 'active' : undefined);

type Section = {
  slug: 'projects' | 'others' | 'photos' | 'magazines';
  label: string;
  element: React.ReactNode;
};

const DEFAULT_SECTION: Section['slug'] = 'projects';

export const AwardsPage: React.FC = () => {
  const navigate = useNavigate();
  const { section } = useParams<{ section?: Section['slug'] }>();
  const awmacLoader = useSelector<RootState, boolean>((state) => state.awmac.awmacLoader);

  const sections: ({ label: string; slug: string; element: JSX.Element } | { label: string; slug: string; element: JSX.Element } | { label: string; slug: string; element: JSX.Element } | { label: string; slug: string; element: JSX.Element })[] = useMemo(
    () => [
      { slug: 'projects', label: 'Projects Awards', element: <div><TabProjectAwards /></div> },
      { slug: 'other', label: 'Other Awards', element: <div><TabOtherAwards /></div> },
      { slug: 'photos', label: 'Event Photos', element: <div><TabEventPhotosAwards /></div> },
      { slug: 'magazine', label: 'Benchmark Magazine', element: <div><TabMagazine /></div> },
    ],
    [],
  );

  const slugs = sections.map((s) => s.slug);
  const isValid = section ? slugs.includes(section) : false;

  useEffect(() => {
    if (!section || !isValid) {
      navigate(`/awards/${DEFAULT_SECTION}`, { replace: true });
    }
  }, [section, isValid, navigate]);

  const current = sections.find((s) => s.slug === section) ?? sections[0];

  return <>
      <Spin spinning={awmacLoader} tip={'Please wait ...'}
            style={{ minHeight: 400 }}
            indicator={<LoadingOutlined style={{ fontSize: 50 }} spin />}>
        <Helmet>
            <title>{current.label}</title>
            <meta
                name="description"
                content="AWMAC - Architectural Woodwork Manufacturers Association of Canada AWARDS"
            />
            <link rel="canonical" href={`https://awmacbc.com/awards/${current.slug}`} />
        </Helmet>
        <DivMinWidth>
            <div style={{ padding: '20px' }}>
                <FlexPadStyled vertical>
                    <LogoBC />
                </FlexPadStyled>
                <Flex style={{ margin: '20px 0' }}>
                    <Breadcrumbs />
                </Flex>
                <h1 style={{ textAlign: 'center', fontFamily: fonts.font2 }}>AWMAC AWARDS OF EXCELLENCE</h1>
                <Nav aria-label="Awards navigation">
                    {sections.map(({ slug, label }) => (
                        <NavItem
                            key={slug}
                            to={`/awards/${slug}`}
                            className={getNavClass}
                            aria-current={section === slug ? 'page' : undefined}
                        >
                            {label}
                        </NavItem>
                    ))}
                </Nav>
                <TabPanelsWrapper>
                    {current.element}
                </TabPanelsWrapper>
            </div>
        </DivMinWidth>
      </Spin>
    </>;
};
